import { useRef, useState } from "react";
import { updateProjectDirectory, type ProjectDirectory } from "@/api/spaces";
import { useSavedTextField } from "./settings-draft-state";

export function useDirectoryEditor(
  projectId: string,
  initial: ProjectDirectory,
) {
  const [directory, setDirectory] = useState(initial);
  const current = useRef(directory);
  current.current = directory;
  async function save(patch: Partial<ProjectDirectory>) {
    const result = await updateProjectDirectory(projectId, initial.id, patch);
    const changes = Object.fromEntries(
      Object.keys(patch).map((key) => [
        key,
        result[key as keyof ProjectDirectory],
      ]),
    );
    current.current = {
      ...current.current,
      ...changes,
      dev_server_confirmed_at: result.dev_server_confirmed_at,
    };
    setDirectory(current.current);
  }
  return { directory, setDirectory, save };
}
export type DirectoryEditor = ReturnType<typeof useDirectoryEditor>;
export type DirectoryTextKey =
  | "repo_url"
  | "default_base_branch"
  | "branch_prefix"
  | "pr_target_branch"
  | "branch_name_template"
  | "dev_server_prepare"
  | "dev_server_command"
  | "dev_server_url"
  | "dev_server_ready_check";
export function useDirectoryText(
  projectId: string,
  editor: DirectoryEditor,
  key: DirectoryTextKey,
) {
  return useSavedTextField({
    draftKey: `project:${projectId}:folder:${editor.directory.id}:${key}`,
    saved: editor.directory[key] ?? "",
    save: (value) => editor.save({ [key]: value }),
  });
}
